/**
 * customPageWire — the wire shape of /api/cms/public/custom-pages/:slug and
 * the conversion to the snapshot shape (`CmsCustomPageIndexEntry`).
 *
 * WHY THE CONVERSION EXISTS
 * -------------------------
 * The public endpoint collapses per-locale columns into ONE flat set of
 * strings for the requested locale:
 *
 *   { slug, route, title, excerpt, body_md, seo, updated_at, published_at, is_draft }
 *
 * The snapshot carries all three locales' per-locale columns:
 *
 *   { title_zhHant, title_zhHans, title_en, excerpt_zhHant, …, body_md_zhHant, …, seo }
 *
 * The sync script performs this conversion while building the snapshot
 * (fetching once per locale and merging). The runtime path needs it too —
 * reading wire data as if it were a snapshot entry leaves every non-requested
 * locale empty.
 *
 * The conversion lives here so the runtime hook and the prerender pass cannot
 * drift.
 *
 * THE `seo` FIELD
 * ---------------
 * The wire's `seo` is FLAT and single-locale — `{ metaTitle, metaDescription,
 * ogImageMediaId, canonicalUrl, robotsDirective }` for the requested locale
 * only, which is how `src/pages/CustomPage.tsx` reads it. The snapshot's
 * `seo` is NESTED per locale — `{ zhHant: {…}, zhHans: {…}, en: {…} }` — the
 * same shape the DB stores, so all three locales' SEO survive the sync
 * instead of the last fetch overwriting the first two.
 *
 * This module is the shared type home for the wire contract: it imports
 * NOTHING from `src/admin/**`, so the public bundle stays free of the admin
 * tree.
 */

/** One custom page as the public endpoint serves it. */
export interface CmsCustomPageWire {
  slug: string
  route: string
  title: string
  excerpt: string
  body_md: string
  seo: Record<string, unknown>
  updated_at: number
  published_at: number | null
  is_draft: boolean
}

/** The detail endpoint's response body. */
export interface CmsCustomPageWireResp {
  locale: string
  custom_page: CmsCustomPageWire
}

/** The list endpoint's response body. */
export interface CmsCustomPageListResp {
  locale: string
  custom_pages: CmsCustomPageWire[]
}

/** One custom page as the build-time snapshot stores it. */
export interface CmsCustomPageIndexEntry {
  slug: string
  route: string
  title_zhHant: string
  title_zhHans: string
  title_en: string
  excerpt_zhHant: string
  excerpt_zhHans: string
  excerpt_en: string
  body_md_zhHant: string
  body_md_zhHans: string
  body_md_en: string
  /** Nested per locale — `{ zhHant: {…}, zhHans: {…}, en: {…} }`. */
  seo: Record<string, unknown>
  updated_at: number
  published_at: number | null
}

/** The per-locale key a locale maps to in the DB columns and in `seo`. */
export type CustomPageLocaleKey = 'zhHant' | 'zhHans' | 'en'

const LOCALE_KEY = (locale: string): CustomPageLocaleKey =>
  locale === 'zh-Hant' ? 'zhHant' : locale === 'zh-Hans' ? 'zhHans' : 'en'

/**
 * The per-locale columns each locale key owns.
 *
 * Written out instead of computed so the entry stays a plain typed object —
 * a computed key of union type widens the literal to a string index and
 * loses the field types.
 */
const TEXT_COLUMNS: Record<
  CustomPageLocaleKey,
  {
    title: 'title_zhHant' | 'title_zhHans' | 'title_en'
    excerpt: 'excerpt_zhHant' | 'excerpt_zhHans' | 'excerpt_en'
    body_md: 'body_md_zhHant' | 'body_md_zhHans' | 'body_md_en'
  }
> = {
  zhHant: { title: 'title_zhHant', excerpt: 'excerpt_zhHant', body_md: 'body_md_zhHant' },
  zhHans: { title: 'title_zhHans', excerpt: 'excerpt_zhHans', body_md: 'body_md_zhHans' },
  en: { title: 'title_en', excerpt: 'excerpt_en', body_md: 'body_md_en' },
}

/**
 * Convert one wire response into a snapshot entry, merging this locale's
 * collapsed values into the per-locale columns. Callers (cms-sync) fetch once
 * per locale and merge by slug so the final entry carries all three locales.
 *
 * `seo` is MERGED, never replaced: each locale contributes its own subtree,
 * so the last fetch cannot erase the other two languages' SEO.
 */
export function customPageWireToEntry(
  raw: CmsCustomPageWire,
  locale: string,
  previous: Partial<CmsCustomPageIndexEntry> | null = null,
): CmsCustomPageIndexEntry {
  const k = LOCALE_KEY(locale)
  const cols = TEXT_COLUMNS[k]
  const prev = previous ?? {}
  const texts = {
    title_zhHant: prev.title_zhHant ?? '',
    title_zhHans: prev.title_zhHans ?? '',
    title_en: prev.title_en ?? '',
    excerpt_zhHant: prev.excerpt_zhHant ?? '',
    excerpt_zhHans: prev.excerpt_zhHans ?? '',
    excerpt_en: prev.excerpt_en ?? '',
    body_md_zhHant: prev.body_md_zhHant ?? '',
    body_md_zhHans: prev.body_md_zhHans ?? '',
    body_md_en: prev.body_md_en ?? '',
  }
  texts[cols.title] = raw.title
  texts[cols.excerpt] = raw.excerpt
  texts[cols.body_md] = raw.body_md
  return {
    slug: raw.slug,
    route: raw.route,
    ...texts,
    seo: { ...(prev.seo ?? {}), [k]: raw.seo },
    updated_at: raw.updated_at,
    published_at: raw.published_at,
  }
}

/**
 * Convert a snapshot entry back into the wire shape for ONE locale.
 *
 * The runtime path needs this: the snapshot is all-locales, but the page
 * renders one locale at a time and reads `seo` flat.
 */
export function customPageEntryToWire(
  entry: CmsCustomPageIndexEntry,
  locale: string,
): CmsCustomPageWire {
  const k = LOCALE_KEY(locale)
  const cols = TEXT_COLUMNS[k]
  const seo = entry.seo ?? {}
  const scoped = seo[k]
  return {
    slug: entry.slug,
    route: entry.route,
    title: entry[cols.title],
    excerpt: entry[cols.excerpt],
    body_md: entry[cols.body_md],
    seo: scoped && typeof scoped === 'object' && !Array.isArray(scoped)
      ? (scoped as Record<string, unknown>)
      : {},
    updated_at: entry.updated_at,
    published_at: entry.published_at,
    is_draft: false,
  }
}
